import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, Field } from "@/components/common";

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
    <div className="w-full max-w-sm space-y-6 fade-up">
      <BrandMark />
      {children}
    </div>
  </div>
);

const PwInput = ({ value, onChange, testid, auto = "new-password" }) => (
  <Input type="password" autoComplete={auto} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" data-testid={testid} required />
);

export function ChangePasswordPage() {
  const { user, reload, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const forced = user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.next.length < 8) return setErr("New password must be at least 8 characters");
    if (f.next !== f.confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      await reload();
      toast.success("Password updated");
      nav("/dashboard", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{forced ? "Set your password" : "Change password"}</h2>
          {forced && (
            <p className="mt-2 flex gap-2 rounded-lg bg-accent/15 p-3 text-sm" data-testid="forced-change-notice">
              <ShieldCheck className="h-4 w-4 shrink-0 text-accent" />
              Welcome, {user?.name}. Please replace your temporary password before continuing.
            </p>
          )}
        </div>
        <Field label={forced ? "Temporary password" : "Current password"}>
          <PwInput value={f.current} onChange={(v) => setF({ ...f, current: v })} testid="current-password-input" auto="current-password" />
        </Field>
        <Field label="New password" hint="At least 8 characters">
          <PwInput value={f.next} onChange={(v) => setF({ ...f, next: v })} testid="new-password-input" />
        </Field>
        <Field label="Confirm new password">
          <PwInput value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} testid="confirm-password-input" />
        </Field>
        {err && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="change-password-error">{err}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="change-password-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save password
        </Button>
        {forced ? (
          <button type="button" onClick={logout} className="w-full text-sm text-muted-foreground hover:text-foreground" data-testid="change-password-logout">Log out</button>
        ) : (
          <Link to="/dashboard" className="flex items-center justify-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Back</Link>
        )}
      </form>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [id, setId] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: id });
      setMsg(data.message);
    } catch (ex) {
      setMsg(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">Forgot password</h2>
          <p className="mt-1 text-sm text-muted-foreground">Enter your mobile number or email. If your account has an email, we'll send a reset link. Otherwise, ask the Admin to reset it.</p>
        </div>
        <Field label="Mobile number or email">
          <Input value={id} onChange={(e) => setId(e.target.value)} className="h-11" data-testid="forgot-identifier-input" required />
        </Field>
        {msg && <p className="rounded-lg bg-secondary px-3 py-2 text-sm" data-testid="forgot-password-message">{msg}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="forgot-password-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send reset link
        </Button>
        <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-muted-foreground" data-testid="back-to-login-link"><ArrowLeft className="h-4 w-4" />Back to sign in</Link>
      </form>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ next: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.next.length < 8) return setErr("Password must be at least 8 characters");
    if (f.next !== f.confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: f.next });
      toast.success("Password reset. Please sign in.");
      nav("/login", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
        <h2 className="text-2xl font-extrabold tracking-tight">Choose a new password</h2>
        <Field label="New password" hint="At least 8 characters">
          <PwInput value={f.next} onChange={(v) => setF({ ...f, next: v })} testid="reset-new-password-input" />
        </Field>
        <Field label="Confirm new password">
          <PwInput value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} testid="reset-confirm-password-input" />
        </Field>
        {err && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="reset-password-error">{err}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="reset-password-submit">Reset password</Button>
      </form>
    </Shell>
  );
}
